import { useState, useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../API';
import styles from './DefinitionEditor.module.css';

/** Format region title: displayName/longName plus (abbreviation/name) only when abbreviation is a valid non-empty string */
function formatRegionTitle(definition) {
    const main = definition?.displayName ?? definition?.longName ?? '';
    const abbr = definition?.abbreviation ?? definition?.name;
    const showAbbr = abbr != null && String(abbr).trim() !== '' && abbr !== 'undefined';
    return showAbbr ? `${main} (${abbr})` : main;
}

/**
 * Editor for item-specific definition modifications.
 * Allows editing definition properties that are specific to this item
 * (e.g., removing landmarks, changing details).
 * Changes are saved to BdsaItem, not to the definition database.
 *
 * Region details use a normalized shape: landmarks (string[]), hemisphere,
 * sliceOrientation, damage (string[] | null), regionCategory (Other/Lesion only).
 * No schema-region-named keys (e.g. no Cerebellum); region type is schemaRegionKey.
 */
const DefinitionEditor = ({
    type, // 'stain' or 'region'
    definition, // Full definition object from BdsaItem or BdsaRegion
    onSave, // Callback with modified definition
    onCancel, // Callback to close editor
    isOpen,
    detailsOnly = false, // When true (region): show but do not edit abbreviation/displayName; only edit details (landmarks, hemisphere, etc.)
    definitionContext = false, // When true: editing global BdsaRegion (e.g. from block); subtitle says changes update the definition for all uses
    readOnly = false, // When true: view only; dialog opens for viewing options but saving/editing is disallowed (e.g. non-quarantined items)
    layout = 'modal', // 'modal' | 'embedded' — embedded has no overlay (for sheet subviews)
    hideChrome = false, // When true: omit header (parent supplies title); footer still shown unless hideFooter
    hideFooter = false, // When true: omit Cancel/Save (parent sticky footer calls actionsRef.save)
    saveLabel = null, // Override primary save button label
    cancelLabel = null, // Override cancel button label (e.g. Discard when dirty)
    subtitle = null, // Optional subtitle override when chrome is shown
    onDirtyChange = null, // (dirty: boolean) => void — for parent discard guards
    actionsRef = null, // { current: { save: () => void } } — parent sticky Save
}) => {
    const [formData, setFormData] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const baselineRef = useRef(null);

    // Fetch schema to determine which fields are from schema (read-only)
    const schemaName = type === 'stain' ? 'stain' : 'region';
    const { data: schemaResponse } = useQuery({
        queryKey: ['bdsa-schema', schemaName],
        queryFn: () => api.get('/bdsa/schema', { query: { name: schemaName, includeDrafts: false } }),
        staleTime: 5 * 60 * 1000,
        enabled: isOpen,
    });

    const latestSchema = schemaResponse?.status === 200 && Array.isArray(schemaResponse.data) && schemaResponse.data.length > 0
        ? schemaResponse.data[schemaResponse.data.length - 1]
        : null;

    // Schema region key for lookups (landmark options, etc.); use definition.schemaRegionKey
    const regionKey = type === 'region' ? (definition?.schemaRegionKey ?? null) : null;

    // Available landmarks for this region (from schema)
    const availableLandmarks = useMemo(() => {
        if (type !== 'region' || !regionKey || !latestSchema) return [];
        const regionDef = latestSchema.definition?.properties?.bdsa_region_ids?.properties?.regions?.properties[regionKey];
        return regionDef?.items?.enum || [];
    }, [type, regionKey, latestSchema]);

    // Region schema properties (hemisphere, sliceOrientation, damage) - same as wizard, exclude 'regions'
    const regionSchemaProperties = useMemo(() => {
        if (type !== 'region' || !latestSchema) return {};
        const regionIdsProps = latestSchema.definition?.properties?.bdsa_region_ids?.properties || {};
        const { regions: _r, ...otherProps } = regionIdsProps;
        return otherProps;
    }, [type, latestSchema]);

    // Other/Lesion schema properties (e.g. regionCategory)
    const otherLesionSchemaProperties = useMemo(() => {
        if (type !== 'region' || !regionKey || !latestSchema) return {};
        if (regionKey !== 'Other' && regionKey !== 'Lesion') return {};
        const regionDef = latestSchema.definition?.properties?.bdsa_region_ids?.properties?.regions?.properties?.[regionKey];
        return regionDef?.properties || {};
    }, [type, regionKey, latestSchema]);

    // Initialize form data from definition (item's region has full definition: hemisphere, orientation, damage, landmarks)
    useEffect(() => {
        if (definition && isOpen) {
            const details = definition.details || {};
            let base;
            if (type === 'stain') {
                // Stain form is driven only by definition.details (stain definition already has the relevant fields)
                base = { ...(definition.details || {}) };
            } else {
                base = {
                    ...details,
                    abbreviation: definition.abbreviation ?? definition.name,
                    displayName: definition.displayName ?? definition.longName,
                };
                if (type === 'region' && regionKey !== null) {
                    base.selectedLandmarks = Array.isArray(details.landmarks) ? [...details.landmarks] : [];
                    base.hemisphere = details.hemisphere ?? '';
                    base.sliceOrientation = details.sliceOrientation ?? '';
                    base.damage = Array.isArray(details.damage) ? [...details.damage] : [];
                    base.regionCategory = details.regionCategory ?? '';
                }
            }
            setFormData(base);
            baselineRef.current = JSON.stringify(base);
            setFieldErrors({});
        } else {
            baselineRef.current = null;
        }
    }, [definition, isOpen, type, regionKey]);

    const isDirty = !readOnly
        && baselineRef.current != null
        && JSON.stringify(formData) !== baselineRef.current;

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Get schema properties for this definition type (region only; stain uses definition.details directly)
    const schemaProperties = useMemo(() => {
        if (!latestSchema || !definition) return {};
        if (type === 'region') {
            return latestSchema.definition?.properties?.bdsa_region_ids?.properties || {};
        }
        return {};
    }, [latestSchema, definition, type]);

    const isOtherOrLesion = regionKey === 'Other' || regionKey === 'Lesion';

    const handleFieldChange = (field, value) => {
        if (readOnly) return;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field
        if (fieldErrors[field]) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated[field];
                return updated;
            });
        }
    };

    const handleSave = () => {
        if (readOnly) return;
        const errors = {};
        if (type === 'region' && !detailsOnly && !formData.abbreviation) errors.abbreviation = 'Abbreviation is required';
        if (type === 'region' && !detailsOnly && !formData.displayName) errors.displayName = 'Display name is required';
        if (type === 'region') {
            if (regionSchemaProperties.hemisphere && !(formData.hemisphere || '').trim()) errors.hemisphere = 'Hemisphere is required';
            if (regionSchemaProperties.sliceOrientation && !(formData.sliceOrientation || '').trim()) errors.sliceOrientation = 'Slice orientation is required';
        }
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        const modifiedDefinition = { ...definition };
        if (type === 'region') {
            const details = {};
            details.landmarks = Array.isArray(formData.selectedLandmarks) ? formData.selectedLandmarks : [];
            if (regionSchemaProperties.hemisphere !== undefined) details.hemisphere = formData.hemisphere || null;
            if (regionSchemaProperties.sliceOrientation !== undefined) details.sliceOrientation = formData.sliceOrientation || null;
            if (regionSchemaProperties.damage !== undefined) details.damage = (Array.isArray(formData.damage) && formData.damage.length > 0) ? formData.damage : null;
            if (isOtherOrLesion && Object.keys(otherLesionSchemaProperties).length > 0) details.regionCategory = formData.regionCategory || null;
            modifiedDefinition.details = details;
            if (!detailsOnly) {
                modifiedDefinition.abbreviation = formData.abbreviation;
                modifiedDefinition.displayName = formData.displayName;
            }
            // Drop schema blob only; keep schemaId + provenance on the item snapshot
            delete modifiedDefinition.schema;
        } else {
            // Stain: persist definition.details with edited values (stain definition shape is the source of truth)
            const details = { ...(definition.details || {}) };
            Object.keys(formData).forEach(k => {
                const v = formData[k];
                if (typeof v === 'boolean') details[k] = v; // keep schema-type marker e.g. Bielschowsky: true
                else if (typeof v === 'object' && v !== null && !Array.isArray(v)) return; // skip nested objects
                else details[k] = (v === '' || (Array.isArray(v) && v.length === 0)) ? null : v;
            });
            modifiedDefinition.details = details;
            delete modifiedDefinition.schema;
        }
        onSave(modifiedDefinition);
    };

    useImperativeHandle(actionsRef, () => ({
        save: handleSave,
        isDirty: () => isDirty,
    }), [isDirty, definition, formData, readOnly, type, detailsOnly, regionSchemaProperties, otherLesionSchemaProperties, isOtherOrLesion]);

    if (!isOpen || !definition) return null;

    const isEmbedded = layout === 'embedded';
    const defaultRegionSubtitle = definition?.bdsaBlockId
        ? 'Define region properties specific to this tissue block. Other blocks can have this region also with different selections.'
        : definitionContext
            ? 'Changes will update the region definition for all uses.'
            : 'Changes made here are specific to this item only. They will not affect the original definition or other items using it.';
    const resolvedSubtitle = subtitle ?? (
        type === 'region' && detailsOnly
            ? defaultRegionSubtitle
            : type === 'stain' && detailsOnly
                ? 'Changes made here are specific to this item only. They will not affect the original definition or other items using it.'
                : null
    );
    const primarySaveLabel = saveLabel || 'Save Changes';
    const resolvedCancelLabel = cancelLabel
        ?? (readOnly ? 'Close' : (isDirty ? 'Discard' : 'Cancel'));

    const body = (
        <>
                {!hideChrome && (
                <div className={styles.header}>
                    <div className={styles.headerTitle}>
                        {type === 'region' && detailsOnly && (definition?.displayName != null || definition?.longName != null) ? (
                            <>
                                <h3>{formatRegionTitle(definition)}</h3>
                                {resolvedSubtitle && (
                                    <span className={styles.headerSubtitle}>{resolvedSubtitle}</span>
                                )}
                            </>
                        ) : type === 'stain' && detailsOnly && definition?.name ? (
                            <>
                                <h3>{definition.name}</h3>
                                {resolvedSubtitle && (
                                    <span className={styles.headerSubtitle}>{resolvedSubtitle}</span>
                                )}
                            </>
                        ) : (
                            <h3>{readOnly ? 'View' : 'Edit'} {type.charAt(0).toUpperCase() + type.slice(1)} for This Item</h3>
                        )}
                    </div>
                    <button className={styles.closeButton} onClick={onCancel} aria-label="Close">×</button>
                </div>
                )}
                <div className={isEmbedded && hideChrome ? styles.contentEmbedded : styles.content}>
                    {!(type === 'region' && detailsOnly) && !(type === 'stain' && detailsOnly) && !definitionContext && (
                        <div className={styles.infoBox}>
                            <strong>Note:</strong> Changes made here are specific to this item only.
                            They will not affect the original definition or other items using it.
                        </div>
                    )}

                    <div className={styles.form}>
                        {type === 'region' && !detailsOnly && (
                            <>
                                <div className={styles.field}>
                                    <label>Abbreviation *</label>
                                    <input
                                        type="text"
                                        value={formData.abbreviation || ''}
                                        onChange={(e) => handleFieldChange('abbreviation', e.target.value)}
                                        className={fieldErrors.abbreviation ? styles.error : ''}
                                        disabled={readOnly}
                                        readOnly={readOnly}
                                    />
                                    {fieldErrors.abbreviation && <span className={styles.errorMessage}>{fieldErrors.abbreviation}</span>}
                                </div>
                                <div className={styles.field}>
                                    <label>Display name *</label>
                                    <input
                                        type="text"
                                        value={formData.displayName || ''}
                                        onChange={(e) => handleFieldChange('displayName', e.target.value)}
                                        className={fieldErrors.displayName ? styles.error : ''}
                                        disabled={readOnly}
                                        readOnly={readOnly}
                                    />
                                    {fieldErrors.displayName && <span className={styles.errorMessage}>{fieldErrors.displayName}</span>}
                                </div>
                            </>
                        )}

                        {/* Hemisphere and Slice Orientation - mandatory, at top of region form */}
                        {type === 'region' && (regionSchemaProperties.hemisphere || regionSchemaProperties.sliceOrientation) && (
                            <div className={styles.field}>
                                <label>Hemisphere &amp; Orientation *</label>
                                <small className={styles.fieldHint}>Required sectioning options for this region</small>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1em', marginTop: '0.35em' }}>
                                    {regionSchemaProperties.hemisphere && (
                                        <div className={styles.field}>
                                            <label>Hemisphere *</label>
                                            <select
                                                value={formData.hemisphere || ''}
                                                onChange={(e) => handleFieldChange('hemisphere', e.target.value)}
                                                className={fieldErrors.hemisphere ? styles.error : ''}
                                                disabled={readOnly}
                                            >
                                                <option value="">-- Select --</option>
                                                {(regionSchemaProperties.hemisphere.enum || ['left', 'right']).map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                            {fieldErrors.hemisphere && <span className={styles.errorMessage}>{fieldErrors.hemisphere}</span>}
                                        </div>
                                    )}
                                    {regionSchemaProperties.sliceOrientation && (
                                        <div className={styles.field}>
                                            <label>Slice Orientation *</label>
                                            <select
                                                value={formData.sliceOrientation || ''}
                                                onChange={(e) => handleFieldChange('sliceOrientation', e.target.value)}
                                                className={fieldErrors.sliceOrientation ? styles.error : ''}
                                                disabled={readOnly}
                                            >
                                                <option value="">-- Select --</option>
                                                {(regionSchemaProperties.sliceOrientation.enum || ['axial', 'coronal', 'sagittal']).map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                            {fieldErrors.sliceOrientation && <span className={styles.errorMessage}>{fieldErrors.sliceOrientation}</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Stain: one field per key in definition.details (stain definition includes the relevant fields) */}
                        {type === 'stain' && (() => {
                            const details = definition?.details || {};
                            const editableEntries = Object.entries(details).filter(([k, v]) =>
                                typeof v !== 'boolean' && (typeof v !== 'object' || v === null || Array.isArray(v))
                            );
                            if (editableEntries.length === 0) return null;
                            const label = (key) => key.replace(/([A-Z])/g, ' $1').replace(/[-_](.)/g, (_, c) => ' ' + c.toUpperCase()).replace(/^./, s => s.toUpperCase());
                            return (
                                <>
                                    {editableEntries.map(([key, _]) => {
                                        const value = formData[key];
                                        const isArray = Array.isArray(value);
                                        if (isArray) return null; // skip array fields for now (or add multi-select later)
                                        return (
                                            <div key={key} className={styles.field}>
                                                <label>{label(key)}</label>
                                                <input
                                                    type="text"
                                                    value={value ?? ''}
                                                    onChange={(e) => handleFieldChange(key, e.target.value)}
                                                    placeholder={key === 'dilution' ? 'e.g., 1:100' : undefined}
                                                    disabled={readOnly}
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                        );
                                    })}
                                </>
                            );
                        })()}

                        {type === 'region' && (
                            <>
                                {/* Landmarks - same style as Damage below */}
                                {!isOtherOrLesion && availableLandmarks.length > 0 && (
                                    <div className={styles.field}>
                                        <label>Landmarks (Optional)</label>
                                        <small className={styles.fieldHint}>Anatomical landmarks typically present in this region</small>
                                        <div className={styles.landmarksList}>
                                            {availableLandmarks.map(landmark => (
                                                <label key={landmark} className={styles.checkbox}>
                                                    <input
                                                        type="checkbox"
                                                        checked={(formData.selectedLandmarks || []).includes(landmark)}
                                                        onChange={(e) => {
                                                            const current = formData.selectedLandmarks || [];
                                                            const updated = e.target.checked
                                                                ? [...current, landmark]
                                                                : current.filter(l => l !== landmark);
                                                            handleFieldChange('selectedLandmarks', updated);
                                                        }}
                                                        disabled={readOnly}
                                                    />
                                                    <span>{landmark}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Damage (Optional) - same flat style as Landmarks, no nested box */}
                                {regionSchemaProperties.damage?.items?.enum && (
                                    <div className={styles.field}>
                                        <label>Damage (Optional)</label>
                                        <small className={styles.fieldHint}>Specifies tissue damage in this region and the cause of it</small>
                                        <div className={styles.landmarksList}>
                                            {(regionSchemaProperties.damage.items.enum || []).map(opt => (
                                                <label key={opt} className={styles.checkbox}>
                                                    <input
                                                        type="checkbox"
                                                        checked={(formData.damage || []).includes(opt)}
                                                        onChange={(e) => {
                                                            const current = formData.damage || [];
                                                            const updated = e.target.checked
                                                                ? [...current, opt]
                                                                : current.filter(v => v !== opt);
                                                            handleFieldChange('damage', updated);
                                                        }}
                                                        disabled={readOnly}
                                                    />
                                                    <span>{opt}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Other/Lesion category */}
                                {isOtherOrLesion && Object.keys(otherLesionSchemaProperties).length > 0 && (
                                    <div className={styles.formSection}>
                                        <div className={styles.sectionHeader}>
                                            <div>
                                                <h4 className={styles.sectionTitle}>{regionKey === 'Other' ? 'Other Region' : 'Lesion'} Category</h4>
                                                <small className={styles.fieldHint}>
                                                    {regionKey === 'Other'
                                                        ? 'Use "Other" for regions not covered by standard anatomical regions'
                                                        : 'Use "Lesion" for pathological lesions or abnormal tissue'}
                                                </small>
                                            </div>
                                        </div>
                                        <div className={styles.sectionContent}>
                                            {Object.entries(otherLesionSchemaProperties).map(([key, prop]) => {
                                                if (key === 'name' || key === 'description') return null;
                                                if (prop.enum && prop.type !== 'array') {
                                                    return (
                                                        <div key={key} className={styles.field}>
                                                            <label>{prop.title || key}</label>
                                                            <select
                                                                value={formData.regionCategory ?? ''}
                                                                onChange={(e) => handleFieldChange('regionCategory', e.target.value)}
                                                                disabled={readOnly}
                                                            >
                                                                <option value="">-- Select --</option>
                                                                {prop.enum.map(opt => (
                                                                    <option key={opt} value={opt}>{opt}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {!hideFooter && (
                <div className={isEmbedded && hideChrome ? styles.footerEmbedded : styles.footer}>
                    <button className={styles.cancelButton} onClick={onCancel}>{resolvedCancelLabel}</button>
                    {!readOnly && <button className={styles.saveButton} onClick={handleSave}>{primarySaveLabel}</button>}
                </div>
                )}
        </>
    );

    if (isEmbedded) {
        return (
            <div className={styles.embedded} data-layout="embedded">
                {body}
            </div>
        );
    }

    return (
        <div className={styles.overlay} onClick={onCancel}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                {body}
            </div>
        </div>
    );
};

export default DefinitionEditor;
